import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PortalHeader, PortalLoading } from '../components/PortalComponents';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { 
  Droplets, 
  Thermometer, 
  ThermometerSnowflake,
  AlertTriangle,
  HelpCircle,
  Phone,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Home,
  Wrench,
  Shield,
  Sparkles
} from 'lucide-react';

// Help Item Component
function HelpItem({ icon: Icon, title, solutions, iconColor = 'text-zinc-400' }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-xl border border-white/10 overflow-hidden bg-white/[0.02]" data-testid="help-item">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 sm:p-5 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 ${iconColor}`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <span className="text-sm sm:text-base font-medium text-white text-left">{title}</span>
        </div>
        <div className="flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-zinc-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-500" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-white/5">
          <ul className="mt-4 space-y-3">
            {solutions.map((solution, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs text-zinc-400">
                  {index + 1}
                </span>
                <span className="text-sm text-zinc-300 leading-relaxed">{solution}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between gap-4 text-left hover:text-white transition-colors"
      >
        <span className="text-sm font-medium text-zinc-300">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4">
          <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function HelpCenter() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleContactSupport = () => {
    // Open WhatsApp with official Manklar support number
    const supportNumber = '34623243967';
    const message = encodeURIComponent('Hola, necesito ayuda con mi equipo Manklar.');
    window.open(`https://wa.me/${supportNumber}?text=${message}`, '_blank');
  };

  const helpItems = [
    {
      icon: Droplets,
      title: 'No sale agua',
      iconColor: 'text-blue-400',
      solutions: [
        'Verifica que el equipo esté conectado a la corriente eléctrica.',
        'Comprueba que la llave de paso de agua esté completamente abierta.',
        'Revisa que la manguera de entrada no esté doblada o aplastada.',
        'Si el problema persiste, contacta con soporte técnico a través de WhatsApp o la aplicación.'
      ]
    },
    {
      icon: Droplets,
      title: 'Sale poca agua',
      iconColor: 'text-cyan-400',
      solutions: [
        'Verifica que no existan dobleces en la manguera de entrada de agua.',
        'Comprueba que la presión del agua de tu hogar sea adecuada.',
        'Revisa si hay alguna obstrucción en la salida del dispensador.',
        'Si el problema continúa, contacta con soporte técnico.'
      ]
    },
    {
      icon: ThermometerSnowflake,
      title: 'El agua no enfría (TIKAL / TIKAL S / CHAAK)',
      iconColor: 'text-sky-400',
      solutions: [
        'Espera al menos 30 minutos después de conectar el equipo para que alcance la temperatura.',
        'Verifica que la ventilación trasera del equipo esté libre de obstrucciones (mínimo 10 cm de espacio).',
        'Asegúrate de que el equipo no esté expuesto directamente al sol.',
        'Comprueba que el interruptor de enfriamiento esté activado.',
        'Nota: El modelo KAJAAL solo dispone de agua del tiempo.'
      ]
    },
    {
      icon: Thermometer,
      title: 'El agua no calienta (TIKAL / TIKAL S / CHAAK)',
      iconColor: 'text-orange-400',
      solutions: [
        'Verifica que la función de agua caliente esté activada (interruptor trasero).',
        'Espera 5-10 minutos para que el agua alcance la temperatura.',
        'Comprueba que el equipo esté correctamente conectado a la corriente.',
        'Nota: Los modelos TIKAL, TIKAL S y CHAAK disponen de agua caliente. El modelo KAJAAL solo dispone de agua del tiempo.',
        'Si no funciona, contacta con soporte técnico.'
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Hay una fuga de agua',
      iconColor: 'text-red-400',
      solutions: [
        'Cierra inmediatamente la llave de paso de agua.',
        'Desconecta el equipo de la corriente eléctrica.',
        'Coloca toallas o un recipiente para contener el agua.',
        'Contacta URGENTEMENTE con soporte técnico a través de WhatsApp.',
        'Recuerda: Con la Garantía Manklar, si la incidencia no se resuelve en 48 horas laborables, se te abonará la siguiente mensualidad del equipo afectado.'
      ]
    },
    {
      icon: HelpCircle,
      title: 'Otros problemas',
      iconColor: 'text-zinc-400',
      solutions: [
        'Si experimentas algún otro problema no listado aquí, no intentes reparar el equipo por tu cuenta.',
        'Toma nota del problema y cualquier mensaje o luz indicadora.',
        'Contacta con nuestro servicio técnico para recibir asistencia personalizada.',
        'Puedes comunicar incidencias mediante la aplicación, WhatsApp o servicio técnico.'
      ]
    }
  ];

  // FAQs actualizadas con información real de Manklar
  const faqs = [
    {
      question: '¿Cuánto cuesta el servicio mensual?',
      answer: `El coste mensual depende del modelo de equipo:\n
• MANKLAR KAJAAL: 29,90€/mes (IVA incluido para hogar)\n
• MANKLAR TIKAL: 34,90€/mes (IVA incluido para hogar)\n
• MANKLAR TIKAL S: 34,90€/mes (IVA incluido para hogar)\n
• MANKLAR CHAAK: 44,90€/mes (IVA incluido para hogar)\n
Para empresas, las mismas cuotas indicando +IVA. Todos los equipos requieren una fianza de 69€.`
    },
    {
      question: '¿Qué incluye el servicio de Manklar?',
      answer: `Tu cuota mensual incluye:\n
• Instalación incluida\n
• Cambio anual de filtros incluido\n
• Mantenimiento preventivo anual incluido\n
• Servicio técnico incluido\n
• Garantía Manklar ilimitada mientras mantengas activo el servicio`
    },
    {
      question: '¿Cada cuánto tiempo se realiza el mantenimiento?',
      answer: `El mantenimiento preventivo está incluido una vez al año e incluye el cambio de filtros.\n
Si necesitas solicitar un mantenimiento o revisión adicional antes del mantenimiento anual, el coste será de 59€.\n
Puedes solicitar el mantenimiento desde la sección "Mantenimiento" de tu portal de cliente.`
    },
    {
      question: '¿Qué es la Garantía Manklar?',
      answer: `La Garantía Manklar cubre averías mientras el cliente mantenga activo el servicio y cumpla las condiciones establecidas.\n
En caso de avería:\n
• Puedes comunicar la incidencia mediante la aplicación, WhatsApp o servicio técnico.\n
• Manklar se compromete a gestionar la incidencia con la máxima rapidez.\n
• Si una avería comunicada no queda resuelta en un plazo máximo de 48 horas laborables, Manklar abonará la siguiente mensualidad correspondiente al equipo afectado.`
    },
    {
      question: '¿Cómo funciona el programa de referidos?',
      answer: `El programa de referidos de Manklar te permite obtener meses gratis:\n
1. Comparte tu código o enlace personal con amigos y familia.\n
2. Tu referido se convierte en cliente de Manklar.\n
3. Cuando tu referido complete 3 meses de servicio activo, pasa a estado VALIDADO.\n
4. Por cada referido validado, recibes 1 mes gratis.\n
5. Al alcanzar 10 referidos validados, obtienes una bonificación adicional de 6 meses gratis.\n
Comparte tu código desde la sección "Referidos".`
    },
    {
      question: '¿Qué modelos de equipo ofrece Manklar?',
      answer: `Manklar ofrece 4 modelos de dispensadores:\n
• MANKLAR KAJAAL: Agua del tiempo. 29,90€/mes.\n
• MANKLAR TIKAL: Agua fría, caliente y del tiempo. Depósito 3,5L. 34,90€/mes.\n
• MANKLAR TIKAL S: Agua fría, caliente y del tiempo. Formato compacto. Depósito 1,7L. 34,90€/mes.\n
• MANKLAR CHAAK: Agua fría, caliente y del tiempo. 8 litros de capacidad de agua fría. 44,90€/mes.`
    },
    {
      question: '¿Cómo puedo cancelar el servicio?',
      answer: `Si deseas cancelar tu suscripción, puedes solicitar la retirada del equipo desde la sección "Mantenimiento" seleccionando "Solicitar retirada".\n
Nuestro equipo se pondrá en contacto contigo para coordinar la recogida del equipo.\n
La fianza de 69€ se devolverá una vez verificado el buen estado del equipo.`
    },
    {
      question: '¿Cómo contacto con soporte técnico?',
      answer: `Puedes contactar con el servicio técnico de Manklar de varias formas:\n
• WhatsApp: +34 623 243 967\n
• A través de la aplicación en la sección "Mantenimiento"\n
• Reportando una incidencia desde tu portal de cliente\n
Horario de atención: Lunes a Viernes 9:00 - 18:00 hrs`
    }
  ];

  const maintenanceTips = [
    {
      icon: Sparkles,
      title: 'Limpia el exterior regularmente',
      description: 'Usa un paño húmedo para limpiar el exterior del dispensador. Evita productos abrasivos que puedan dañar la superficie.'
    },
    {
      icon: Droplets,
      title: 'Vacía la bandeja de goteo',
      description: 'Retira y limpia la bandeja de goteo al menos una vez por semana para evitar acumulación de residuos.'
    },
    {
      icon: Shield,
      title: 'Mantén la ventilación libre',
      description: 'Asegúrate de que la parte trasera del equipo tenga al menos 10 cm de espacio libre para una correcta ventilación.'
    },
    {
      icon: Wrench,
      title: 'No realices reparaciones',
      description: 'Si detectas algún problema, contacta con soporte técnico. No intentes reparar el equipo para no perder la Garantía Manklar.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303]">
      <PortalHeader title="Centro de Ayuda" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Title */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-light text-white font-['Outfit'] mb-3">
            Centro de Ayuda
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 max-w-md mx-auto">
            Encuentra soluciones rápidas a los problemas más comunes con tu equipo Manklar
          </p>
        </div>

        {/* WhatsApp CTA - Prominent */}
        <div className="mb-8 p-4 sm:p-6 rounded-xl bg-gradient-to-r from-[#25D366]/10 to-transparent border border-[#25D366]/30 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <h3 className="text-base font-medium text-white mb-1">Soporte Técnico WhatsApp</h3>
            <p className="text-sm text-zinc-400">Respuesta rápida de nuestro equipo técnico</p>
          </div>
          <Button
            onClick={handleContactSupport}
            className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white px-6"
            data-testid="whatsapp-support-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            +34 623 243 967
          </Button>
        </div>

        {/* Warranty Info Box */}
        <div className="mb-8 p-4 sm:p-6 rounded-xl bg-[#0A0A0A] border border-white/10">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-base font-medium text-white mb-2">Garantía Manklar</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                La Garantía Manklar cubre averías mientras mantengas activo el servicio y cumplas las condiciones establecidas. 
                Si comunicas una avería y no queda resuelta en un plazo máximo de <span className="text-white font-medium">48 horas laborables</span>, 
                Manklar abonará la siguiente mensualidad correspondiente al equipo afectado.
              </p>
            </div>
          </div>
        </div>

        {/* Troubleshooting Section */}
        <section className="mb-10">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-zinc-400" />
            Solución de Problemas
          </h2>
          <div className="space-y-3 sm:space-y-4">
            {helpItems.map((item) => (
              <HelpItem
                key={item.title}
                icon={item.icon}
                title={item.title}
                solutions={item.solutions}
                iconColor={item.iconColor}
              />
            ))}
          </div>
        </section>

        {/* Maintenance Tips Section */}
        <section className="mb-10">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-zinc-400" />
            Tips de Mantenimiento
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {maintenanceTips.map((tip) => {
              const Icon = tip.icon;
              return (
                <div key={tip.title} className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-zinc-400" />
                    </div>
                    <h4 className="text-sm font-medium text-white">{tip.title}</h4>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Service Info Section */}
        <section className="mb-10">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-zinc-400" />
            Qué Incluye Tu Servicio
          </h2>
          <div className="p-4 sm:p-6 rounded-xl bg-white/[0.02] border border-white/10">
            <ul className="space-y-3">
              {[
                'Instalación incluida',
                'Cambio anual de filtros incluido',
                'Mantenimiento preventivo anual incluido',
                'Servicio técnico incluido',
                'Garantía Manklar ilimitada mientras mantengas activo el servicio'
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-zinc-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="mb-10">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-zinc-400" />
            Preguntas Frecuentes
          </h2>
          <div className="p-4 sm:p-6 rounded-xl bg-white/[0.02] border border-white/10">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Contact Support CTA */}
        <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-[#0f0f0f] to-[#0a0a0a] border border-white/10 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl sm:text-2xl font-medium text-white font-['Outfit'] mb-2">
            ¿Necesitas más ayuda?
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 mb-6 max-w-md mx-auto">
            Nuestro equipo de soporte técnico está disponible para ayudarte con cualquier problema.
          </p>
          <Button
            onClick={handleContactSupport}
            className="w-full sm:w-auto h-12 sm:h-14 px-8 bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium rounded-xl text-base sm:text-lg"
            data-testid="contact-support-btn"
          >
            <MessageCircle className="w-5 h-5 mr-3" />
            Contactar con Servicio Técnico
          </Button>
        </div>

        {/* Additional Info */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-xs sm:text-sm text-zinc-500">
            Horario de atención: Lunes a Viernes 9:00 - 18:00 hrs
          </p>
        </div>
      </main>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}
